import React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";

/**
 * CommonDialog - A reusable dialog component for all popup dialogs
 * 
 * @param {boolean} open - Whether the dialog is open
 * @param {function} onClose - Function to call when dialog should close
 * @param {string} title - Dialog title text (emoji should be included in this string)
 * @param {ReactNode} children - Content to display in the dialog
 * @param {ReactNode} actions - Optional actions (buttons) to display at the bottom
 * @param {string} maxWidth - Material-UI maxWidth prop (xs, sm, md, lg, xl, false)
 * @param {boolean} fullWidth - Whether dialog should take full width
 * @param {object} PaperProps - Additional props for Paper component
 * @param {string} className - Additional CSS class for the dialog
 * @param {object} titleStyle - Additional styles for the title (or object with className)
 * @param {object} contentStyle - Additional styles for the content (or object with className)
 * @param {object} actionsStyle - Additional styles for the actions (or object with className)
 * @param {boolean} showCloseButton - Whether to show the close button (default: true)
 * @param {function} onCloseButtonClick - Custom handler for close button (defaults to onClose)
 */
const CommonDialog = ({
  open,
  onClose,
  title,
  children,
  actions,
  maxWidth = "sm",
  fullWidth = true,
  PaperProps = {},
  className = "",
  titleStyle = {},
  contentStyle = {},
  actionsStyle = {},
  showCloseButton = true,
  onCloseButtonClick = null
}) => {
  // Default PaperProps styling
  const defaultPaperProps = {
    style: {
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      borderRadius: '12px',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
      maxHeight: '90vh',
      display: 'flex',
      flexDirection: 'column',
      ...PaperProps.style
    },
    ...PaperProps
  };

  // Extract className from style objects if present
  const titleClassName = titleStyle.className || '';
  const contentClassName = contentStyle.className || '';
  const actionsClassName = actionsStyle.className || '';
  
  // Default title styling (merge styles, exclude className)
  const { className: _, ...titleStyleWithoutClass } = titleStyle;
  const defaultTitleStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 18px',
    borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
    flexShrink: 0,
    position: 'relative',
    width: '100%',
    boxSizing: 'border-box',
    ...titleStyleWithoutClass
  };

  // Default content styling (merge styles, exclude className)
  const { className: __, ...contentStyleWithoutClass } = contentStyle;
  const defaultContentStyle = {
    padding: actions ? '20px 24px' : 0,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    ...contentStyleWithoutClass
  };

  // Extract className from actionsStyle
  const { className: ___, ...actionsStyleWithoutClass } = actionsStyle;
  const defaultActionsStyle = {
    ...actionsStyleWithoutClass
  };

  const handleClose = onCloseButtonClick || onClose;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      PaperProps={defaultPaperProps}
      className={className}
    >
      <DialogTitle 
        style={defaultTitleStyle}
        className={titleClassName || undefined}
      >
        <span style={{ fontSize: '1.1rem', fontWeight: 'bold', flex: 1 }}>
          {title}
        </span>
        {showCloseButton && (
          <IconButton
            onClick={handleClose}
            size="small"
            style={{
              color: '#666',
              position: 'absolute',
              right: '8px',
              top: '50%',
              transform: 'translateY(-50%)',
              margin: 0,
              padding: '8px'
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent 
        style={defaultContentStyle}
        className={contentClassName || undefined}
      >
        {children}
      </DialogContent>
      {actions && (
        <DialogActions 
          style={defaultActionsStyle}
          className={actionsClassName || undefined}
        >
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default CommonDialog;

